import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { CustomerDocument } from '@/models/customer.ts';

// Define the document types based on the API contract
type DocumentType = 'id_proof' | 'address_proof' | 'income_proof';
type DocumentStatus = 'pending' | 'verified' | 'rejected';

// Interface for document upload request
interface DocumentUploadRequest {
    name: string;
    type: DocumentType;
    document: File;
    rejection_reason?: string;
}

// Interface for updating document status
interface UpdateDocumentStatusRequest {
    status: DocumentStatus;
    rejection_reason?: string;
}

// Get all documents for a customer
export const useCustomerDocuments = (customerId: string) => {
    return useQuery<ApiResponse<CustomerDocument[]>>({
        queryKey: ['customer-documents', customerId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<CustomerDocument[]>>(
                API_ENDPOINTS.CRM.DOCUMENTS.LIST(customerId)
            );
            return data;
        },
        enabled: !!customerId,
    });
};

// Get a specific document
export const useCustomerDocument = (customerId: string, documentId: string) => {
    return useQuery<ApiResponse<CustomerDocument>>({
        queryKey: ['customer-documents', customerId, documentId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<CustomerDocument>>(
                API_ENDPOINTS.CRM.DOCUMENTS.DETAILS(customerId, documentId)
            );
            return data;
        },
        enabled: !!customerId && !!documentId,
    });
};

// Upload a document
export const useUploadDocument = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerDocument>, Error, DocumentUploadRequest>({
        mutationFn: async documentData => {
            // Create a FormData object to send the file
            const formData = new FormData();
            formData.append('name', documentData.name);
            formData.append('type', documentData.type);
            formData.append('document', documentData.document);

            if (documentData.rejection_reason) {
                formData.append('rejection_reason', documentData.rejection_reason);
            }

            const response = await httpClient.post<ApiResponse<CustomerDocument>>(
                API_ENDPOINTS.CRM.DOCUMENTS.UPLOAD(customerId),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
        },
    });
};

// Delete a document
export const useDeleteDocument = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: async documentId => {
            await httpClient.delete(API_ENDPOINTS.CRM.DOCUMENTS.DELETE(customerId, documentId));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
        },
    });
};

// Update document status
export const useUpdateDocumentStatus = (customerId: string, documentId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<CustomerDocument>, Error, UpdateDocumentStatusRequest>({
        mutationFn: async statusData => {
            const response = await httpClient.put<ApiResponse<CustomerDocument>>(
                API_ENDPOINTS.CRM.DOCUMENTS.UPDATE_STATUS(customerId, documentId),
                statusData
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-documents', customerId] });
            queryClient.invalidateQueries({
                queryKey: ['customer-documents', customerId, documentId],
            });
        },
    });
};

// Get document view URL
export const getDocumentViewUrl = (customerId: string, documentId: string) => {
    return API_ENDPOINTS.CRM.DOCUMENTS.VIEW(customerId, documentId);
};

// View a document in browser (for viewable file types)
export const viewDocument = async (customerId: string, documentId: string) => {
    try {
        const response = await httpClient.get(
            API_ENDPOINTS.CRM.DOCUMENTS.VIEW(customerId, documentId)
        );

        // Check if response contains a presigned URL (S3) or is a direct response (local)
        if (response.data.success && response.data.data?.url) {
            // S3 presigned URL - open directly
            window.open(response.data.data.url, '_blank');
        } else {
            // Local storage - use the original endpoint
            const viewUrl = getDocumentViewUrl(customerId, documentId);
            window.open(viewUrl, '_blank');
        }
    } catch (error) {
        console.error('Error viewing document:', error);
        throw error;
    }
};

// Get document download URL
export const getDocumentDownloadUrl = (customerId: string, documentId: string) => {
    return API_ENDPOINTS.CRM.DOCUMENTS.DOWNLOAD(customerId, documentId);
};

// Download a document using httpClient
export const downloadDocument = async (customerId: string, documentId: string) => {
    try {
        const response = await httpClient.get(
            API_ENDPOINTS.CRM.DOCUMENTS.DOWNLOAD(customerId, documentId)
        );

        // Check if response contains a presigned URL (S3) or is a direct response (local)
        if (response.data.success && response.data.data?.url) {
            // S3 presigned URL - create download link directly
            const link = document.createElement('a');
            link.href = response.data.data.url;
            link.setAttribute('download', response.data.data.filename || 'document');
            link.setAttribute('target', '_blank');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            // Local storage - handle as blob
            const blobResponse = await httpClient.get(
                API_ENDPOINTS.CRM.DOCUMENTS.DOWNLOAD(customerId, documentId),
                {
                    responseType: 'blob',
                }
            );

            // Create a blob URL from the response data
            const blob = new Blob([blobResponse.data]);
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element to trigger the download
            const link = document.createElement('a');
            link.href = url;

            // Try to get the filename from the Content-Disposition header
            const contentDisposition = blobResponse.headers['content-disposition'];
            let filename = 'document';

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]*)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }

        return true;
    } catch (error) {
        console.error('Error downloading document:', error);
        throw error;
    }
};
